import React from 'react';
import { Card } from 'primereact/card';
import { Link, useNavigate, useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { ModernButton } from '../../components/ui/ModernButton';

export const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="not-found-page">
      <div className="not-found-container">
        <Card className="not-found-card">
          <div className="not-found-content">
            <Link to="/" className="not-found-logo-link">
              <img src="/ew-color-logo_crop.png" alt="EquityWorkx" className="not-found-logo" />
            </Link>
            <div className="not-found-code">404</div>
            <h2>Page Not Found</h2>
            <p>
              The page you're looking for doesn't exist or may have been moved.
            </p>
            <div className="not-found-actions">
              <ModernButton
                variant="primary"
                icon="pi pi-home"
                onClick={() => navigate('/')}
              >
                Back to Home
              </ModernButton>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Rendered by react-router itself when a route throws, so it cannot be a
// React error boundary component that wraps children.
export const RouteErrorPage: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  console.error('Route error:', error);

  // A thrown 404 response should read as a missing page, not a crash
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <div className="not-found-page">
      <div className="not-found-container">
        <Card className="not-found-card">
          <div className="not-found-content">
            <Link to="/" className="not-found-logo-link">
              <img src="/ew-color-logo_crop.png" alt="EquityWorkx" className="not-found-logo" />
            </Link>
            <div className="not-found-icon">
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#e74c3c' }}></i>
            </div>
            <h2>Something Went Wrong</h2>
            <p>
              We're sorry, but this page failed to load. Please try again, or
              head back home and start over.
            </p>
            <div className="not-found-actions">
              <ModernButton
                variant="primary"
                icon="pi pi-refresh"
                onClick={() => window.location.reload()}
              >
                Try Again
              </ModernButton>
              <ModernButton
                variant="outline"
                icon="pi pi-home"
                onClick={() => navigate('/')}
              >
                Back to Home
              </ModernButton>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Add some basic styling
const styles = `
.not-found-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
}

.not-found-card {
  max-width: 500px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: none;
}

.not-found-content {
  text-align: center;
  padding: 2rem;
}

.not-found-logo-link {
  display: inline-block;
  margin-bottom: 1.5rem;
}

.not-found-logo {
  height: 48px;
  width: auto;
}

.not-found-code {
  color: #667eea;
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.not-found-icon {
  margin-bottom: 1.5rem;
}

.not-found-content h2 {
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1.8rem;
}

.not-found-content p {
  color: #7f8c8d;
  margin-bottom: 1rem;
  line-height: 1.6;
}

.not-found-actions {
  margin-top: 2rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}
